import { useState } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import {
  Box,
  Container,
  Stack,
  Typography,
  Button,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon
} from '@mui/icons-material';
import FilterSelectInput from 'src/common/FilterSelectInput';
import RequestorListTable from 'src/components/requestors/RequestorListTable';
import NewRequestor from 'src/components/requestors/NewRequestor';
import useAuth from 'src/context/AuthContext';
import { isAdminUser } from 'src/utils/getInitials';
import useAdminSiteFilter from 'src/hooks/useAdminSiteFilter';
import usePersistentListFilters from 'src/hooks/usePersistentListFilters';
import ListRefreshButton from 'src/common/ListRefreshButton';
import ColumnSearch from 'src/common/ColumnSearch';

const Requestors = ({ location }) => {
  const {
    site, setSite
  } = usePersistentListFilters();
  const theme = useTheme();
  const { getUser } = useAuth();
  const {
    isAdmin,
    siteFilter,
    setSiteFilter,
    siteOptions
  } = useAdminSiteFilter(site);
  const [openModal, setOpenModal] = useState(false);
  const [searchFilters, setSearchFilters] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <>
      <Helmet>
        <title>Requestors | Retrack FMS</title>
      </Helmet>

      <Box
        className="transaction-style-page transaction-style-duplicate-header"
        sx={{
          backgroundColor: '#FFFFFF',
          minHeight: 'calc(100vh - 64px)',
          height: { xs: 'auto', md: 'calc(100vh - 64px)' },
          overflow: { xs: 'auto', md: 'hidden' },
          py: 2
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
          }}
        >
          <Stack
            direction="column"
            alignItems="stretch"
            justifyContent="space-between"
            spacing={3}
            sx={{ py: 2, mb: 2, flexShrink: 0 }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: '#111827',
                  letterSpacing: '-0.02em',
                  mb: 0.5
                }}
              >
                REQUESTORS
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 500 }}
              >
                Manage requestor users and configurations.
              </Typography>
            </Box>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="space-between"
              useFlexGap
              flexWrap={{ xs: 'nowrap', sm: 'wrap' }}
              sx={{ width: '100%' }}
            >
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <ColumnSearch
                  fields={[
                    { key: 'user__username', label: 'User Name', defaultChecked: true },
                    { key: 'emp_id', label: 'Employee ID' }
                  ]}
                  values={searchFilters}
                  onApply={(values) => setSearchFilters(values)}
                  onClear={() => setSearchFilters({})}
                />
                <ListRefreshButton onRefresh={() => setRefreshKey((value) => value + 1)} />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                {isAdmin && (
                  <FilterSelectInput
                    label="Site"
                    name="site"
                    value={siteFilter}
                    data={siteOptions}
                    onInputChange={(value) => { setSiteFilter(value); setSite(value); }}
                    sx={{ width: { xs: '100%', sm: 220 }, mr: 0 }}
                  />
                )}

                {isAdminUser(getUser) && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenModal(true)}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                      minWidth: 180,
                      whiteSpace: 'nowrap',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      backgroundColor: theme.palette.primary.main,
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                        boxShadow: '0 6px 16px rgba(0,0,0,0.15)'
                      }
                    }}
                  >
                    Add Requestor
                  </Button>
                )}
              </Stack>
            </Stack>
          </Stack>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden'
            }}
          >
            <RequestorListTable
              refreshData={refreshKey || location.state?.fromDashboard}
              searchFilters={searchFilters}
              siteFilter={siteFilter}
            />
          </Box>

          <NewRequestor
            open={openModal}
            handleClose={() => setOpenModal(false)}
          />
        </Container>
      </Box>
    </>
  );
};

Requestors.propTypes = {
  location: PropTypes.object
};

export default Requestors;
